/**
 * Hit again (Phase 38) ↔ draft mapping, on the verdict satellite: the
 * per-drop rule, its own min sample and the action — how the new amount is
 * made (same · +X % of the drop · +X % of the base · fixed), its cap and
 * how many times a chain may hit, when it may go out (18/10/2026) and its
 * texts (21/10/2026 — moved here from the Texts policy) and its SMSWiser
 * DUPLICATE option (22/10/2026, on by default). An advanced tree is preserved by
 * OMISSION on save, like the terrible rule.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DEFAULT_HIT_ACTION, DROPS_SOURCE, type DropGenHitAction, type DropGenVerdictDetail, parseHitTexts, parseVerdictSources } from '@trafficpulse/shared';

import type { DraftHitAgain } from '../../../../drop-generator.types';
import { flatRuleToTree, parseFlatRule } from './verdict-draft';

export const DEFAULT_HIT_AGAIN: DraftHitAgain = {
  enabled: false,
  rule: { join: 'AND', rows: [], advanced: false },
  minSampleSent: 0,
  mode: DEFAULT_HIT_ACTION.mode,
  pct: DEFAULT_HIT_ACTION.pct,
  fixed: null,
  cap: null,
  maxTimes: DEFAULT_HIT_ACTION.maxTimes,
  when: 'WINDOW',
  texts: null,
  duplicate: true,
  source: DROPS_SOURCE,
};

export function hitAgainToDraft(v: DropGenVerdictDetail | null): DraftHitAgain {
  if (!v) return { ...DEFAULT_HIT_AGAIN };
  return {
    enabled: v.hitAgainEnabled ?? v.hitAgainJson != null,
    rule: parseFlatRule(v.hitAgainJson ?? null),
    minSampleSent: v.hitMinSampleSent ?? DEFAULT_HIT_AGAIN.minSampleSent,
    mode: v.hitAmountMode ?? DEFAULT_HIT_AGAIN.mode,
    pct: v.hitAmountPct ?? DEFAULT_HIT_AGAIN.pct,
    fixed: v.hitAmountFixed ?? null,
    cap: v.hitMaxAmount ?? null,
    maxTimes: v.hitMaxTimes ?? DEFAULT_HIT_AGAIN.maxTimes,
    when: v.hitWhen === 'CONTAINER' ? 'CONTAINER' : 'WINDOW',
    texts: parseHitTexts(v.hitTextsJson),
    duplicate: v.hitForceSend ?? true,
    source: parseVerdictSources(v.sourcesJson).hitAgain,
  };
}

/** The verdict PUT's hit-again fields; an advanced tree is simply not sent. */
export function hitAgainToBody(d: DraftHitAgain): Record<string, unknown> {
  return {
    hitAgainEnabled: d.enabled,
    hitMinSampleSent: d.minSampleSent,
    hitAmountMode: d.mode,
    hitAmountPct: d.pct,
    hitAmountFixed: d.fixed,
    hitMaxAmount: d.cap,
    hitMaxTimes: d.maxTimes,
    hitWhen: d.when,
    hitTextsJson: d.texts,
    hitForceSend: d.duplicate,
    ...(d.rule.advanced ? {} : { hitAgainJson: flatRuleToTree(d.rule) }),
  };
}

/** The action as the shared arithmetic (and the engine) reads it. */
export const hitActionOf = (d: DraftHitAgain): DropGenHitAction => ({
  mode: d.mode,
  pct: d.pct,
  fixed: d.fixed,
  cap: d.cap,
  maxTimes: d.maxTimes,
});

/** True once a rule exists — the step's "on". */
export const hitAgainOn = (d: DraftHitAgain): boolean => d.rule.advanced || d.rule.rows.length > 0;
